document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const theme = window.BBTheme || {};
const routes = theme.routes || {};

function getDialog(selector) {
  if (!selector) return null;
  return document.querySelector(selector);
}

function openDialog(dialog) {
  if (!dialog || dialog.open) return;
  dialog.showModal();
  document.body.classList.add('is-locked');
  const focusTarget = dialog.querySelector('input, button, a[href]');
  window.setTimeout(() => focusTarget?.focus(), 30);
}

function closeDialog(dialog) {
  if (!dialog?.open) return;
  dialog.close();
  if (!document.querySelector('dialog[open]')) document.body.classList.remove('is-locked');
}

function showToast(message) {
  const toast = document.querySelector('[data-toast]');
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add('is-visible');
  window.clearTimeout(showToast.timeout);
  showToast.timeout = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
}

function formatMoney(cents, format = theme.moneyFormat || '${{amount}}') {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = Number(cents || 0);
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;

  const withDelimiter = (number, precision = 2, thousands = ',', decimal = '.') => {
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? parts.join(decimal) : parts[0];
  };

  const match = format.match(placeholder);
  if (!match) return withDelimiter(value);

  let amount = '';
  switch (match[1]) {
    case 'amount_no_decimals': amount = withDelimiter(value, 0); break;
    case 'amount_with_comma_separator': amount = withDelimiter(value, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': amount = withDelimiter(value, 0, '.', ','); break;
    case 'amount':
    default: amount = withDelimiter(value, 2); break;
  }
  return format.replace(placeholder, amount);
}

function initHeader() {
  const header = document.querySelector('[data-header]');
  if (!header) return;
  const update = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
  update();
  window.addEventListener('scroll', update, { passive: true });
}

function initCampaignCarousels(scope = document) {
  const roots = Array.from(scope.querySelectorAll?.('[data-campaign-carousel]') || []);
  if (scope.matches?.('[data-campaign-carousel]')) roots.unshift(scope);

  roots.forEach((carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;

    const slides = Array.from(carousel.querySelectorAll('[data-campaign-slide]'));
    if (slides.length < 2) return;

    const dots = Array.from(carousel.querySelectorAll('[data-carousel-dot]'));
    const previous = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    const current = carousel.querySelector('[data-carousel-current]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = carousel.dataset.autoplay === 'true' && !reducedMotion;
    const interval = Math.max(4, Number(carousel.dataset.autoplaySeconds || 6)) * 1000;
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;
    let pointerStart;

    const stop = () => {
      window.clearTimeout(timer);
      timer = undefined;
    };

    const schedule = () => {
      stop();
      if (!autoplay || document.hidden) return;
      timer = window.setTimeout(() => show(activeIndex + 1), interval);
    };

    const show = (requestedIndex) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });

      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      schedule();
    };

    previous?.addEventListener('click', () => show(activeIndex - 1));
    next?.addEventListener('click', () => show(activeIndex + 1));
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.carouselDot))));

    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', schedule);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', () => window.setTimeout(() => {
      if (!carousel.contains(document.activeElement)) schedule();
    }, 0));
    carousel.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button, a')) return;
      pointerStart = event.clientX;
    });
    carousel.addEventListener('pointerup', (event) => {
      if (pointerStart === undefined) return;
      const distance = event.clientX - pointerStart;
      pointerStart = undefined;
      if (Math.abs(distance) > 42) show(activeIndex + (distance < 0 ? 1 : -1));
    });
    carousel.addEventListener('pointercancel', () => { pointerStart = undefined; });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else schedule();
    });

    carousel.BBCarousel = { show };
    carousel.dataset.carouselReady = 'true';
    show(activeIndex);
  });
}

function initRevealAnimations(scope = document) {
  const elements = scope.querySelectorAll('[data-reveal]:not(.is-visible)');
  if (!elements.length) return;

  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -30px' });

  elements.forEach((element, index) => {
    element.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
    observer.observe(element);
  });
}

function showProductMedia(gallery, id) {
  const media = Array.from(gallery.querySelectorAll('[data-media-id]'));
  const thumbnails = Array.from(gallery.querySelectorAll('[data-media-target]'));
  const target = media.find((item) => String(item.dataset.mediaId) === String(id));
  if (!target) return;

  media.forEach((item) => {
    const active = item === target;
    item.hidden = !active;
    item.classList.toggle('is-active', active);
    if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
  });

  thumbnails.forEach((thumb) => {
    const active = String(thumb.dataset.mediaTarget) === String(id);
    thumb.classList.toggle('is-active', active);
    thumb.setAttribute('aria-pressed', String(active));
  });
}

function moveProductMedia(gallery, direction) {
  const media = Array.from(gallery.querySelectorAll('[data-media-id]'));
  if (!media.length) return;
  const currentIndex = Math.max(0, media.findIndex((item) => !item.hidden));
  const nextIndex = direction === 'next'
    ? (currentIndex + 1) % media.length
    : (currentIndex - 1 + media.length) % media.length;
  showProductMedia(gallery, media[nextIndex].dataset.mediaId);
}

function updateProductVariant(root) {
  const variantsElement = root.querySelector('[data-product-variants]');
  if (!variantsElement) return;

  let variants = [];
  try { variants = JSON.parse(variantsElement.textContent); } catch (_) { return; }

  const values = Array.from(root.querySelectorAll('[data-product-option]')).map((select) => select.value);
  const variant = variants.find((item) => item.options.every((option, index) => option === values[index]));
  const idInput = root.querySelector('[data-variant-id]');
  const addButton = root.querySelector('[data-add-to-cart]');
  const status = root.querySelector('[data-product-status]');
  const currentPrice = root.querySelector('[data-price-current]');
  const comparePrice = root.querySelector('[data-price-compare]');

  if (!variant) {
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = theme.strings?.unavailable || 'Unavailable';
    }
    if (status) status.textContent = 'This combination is unavailable';
    return;
  }

  if (idInput) idInput.value = variant.id;
  if (currentPrice) currentPrice.textContent = formatMoney(variant.price);
  if (comparePrice) {
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    comparePrice.hidden = !onSale;
    comparePrice.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
  }
  if (addButton) {
    addButton.disabled = !variant.available;
    addButton.textContent = variant.available
      ? (theme.strings?.addToCart || 'Add to bag')
      : (theme.strings?.soldOut || 'Sold out');
  }
  if (status) status.textContent = variant.available ? 'Ready to dispatch' : 'Currently unavailable';

  const url = new URL(window.location.href);
  url.searchParams.set('variant', variant.id);
  window.history.replaceState({}, '', url.toString());
}

async function refreshCart(openAfter = false) {
  const root = routes.root || '/';
  const response = await fetch(`${root}?sections=cart-drawer,header`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to refresh cart');
  const sections = await response.json();

  if (sections['cart-drawer']) {
    const documentFragment = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const nextDrawer = documentFragment.querySelector('#CartDrawer');
    const currentDrawer = document.querySelector('#CartDrawer');
    if (nextDrawer && currentDrawer) currentDrawer.replaceWith(nextDrawer);
  }

  if (sections.header) {
    const headerFragment = new DOMParser().parseFromString(sections.header, 'text/html');
    const newCount = headerFragment.querySelector('[data-cart-count]');
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      if (newCount && Number(newCount.textContent) > 0) {
        count.textContent = newCount.textContent;
        count.hidden = false;
      } else {
        count.textContent = '0';
        count.hidden = true;
      }
    });
  }

  if (openAfter) openDialog(document.querySelector('#CartDrawer'));
}

async function addProduct(form, submitter) {
  if (submitter) {
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
  }

  try {
    const response = await fetch(form.action || routes.cartAdd, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Unable to add item');
    }
    await refreshCart(true);
    showToast(theme.strings?.added || 'Added to your bag');
  } catch (error) {
    showToast(error.message || theme.strings?.error || 'Something went wrong.');
  } finally {
    if (submitter) {
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
    }
  }
}

async function changeCartLine(line, quantity, button) {
  if (button) button.disabled = true;
  try {
    const response = await fetch(routes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)) })
    });
    if (!response.ok) throw new Error('Unable to update bag');
    await refreshCart(true);
  } catch (error) {
    showToast(error.message || theme.strings?.error || 'Something went wrong.');
    if (button) button.disabled = false;
  }
}

document.addEventListener('click', (event) => {
  const openButton = event.target.closest('[data-dialog-open]');
  if (openButton) {
    openDialog(getDialog(openButton.dataset.dialogOpen));
    return;
  }

  const closeButton = event.target.closest('[data-dialog-close]');
  if (closeButton) {
    closeDialog(closeButton.closest('dialog'));
    return;
  }

  const cartOpen = event.target.closest('[data-cart-open]');
  if (cartOpen) {
    openDialog(document.querySelector('#CartDrawer'));
    return;
  }

  const mediaButton = event.target.closest('[data-media-target]');
  if (mediaButton) {
    const gallery = mediaButton.closest('[data-product-gallery]');
    showProductMedia(gallery, mediaButton.dataset.mediaTarget);
    return;
  }

  const galleryArrow = event.target.closest('[data-gallery-direction]');
  if (galleryArrow) {
    moveProductMedia(galleryArrow.closest('[data-product-gallery]'), galleryArrow.dataset.galleryDirection);
    return;
  }

  const quantityButton = event.target.closest('[data-quantity-change]');
  if (quantityButton) {
    const input = quantityButton.closest('.quantity')?.querySelector('[data-quantity-input]');
    if (!input) return;
    input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) + Number(quantityButton.dataset.quantityChange)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const cartLineButton = event.target.closest('[data-cart-line][data-cart-quantity]');
  if (cartLineButton) {
    changeCartLine(cartLineButton.dataset.cartLine, cartLineButton.dataset.cartQuantity, cartLineButton);
  }
});

document.addEventListener('click', (event) => {
  if (event.target.matches('dialog[open]')) closeDialog(event.target);
});

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-product-option]')) {
    const root = event.target.closest('[data-product-root]');
    if (root) updateProductVariant(root);
  }

  if (event.target.matches('[data-sort-select]')) {
    event.target.closest('form')?.submit();
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('.js-product-form');
  if (!form) return;
  if (event.submitter && event.submitter.name !== 'add') return;
  event.preventDefault();
  addProduct(form, event.submitter);
});

document.addEventListener('shopify:section:load', (event) => {
  initRevealAnimations(event.target);
  initCampaignCarousels(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-campaign-slide]');
  const carousel = slide?.closest('[data-campaign-carousel]');
  carousel?.BBCarousel?.show(Number(slide.dataset.slideIndex));
});

initHeader();
initCampaignCarousels();
initRevealAnimations();
document.documentElement.classList.add('reveal-ready');
